import { compact as _compact } from 'lodash';
import { Notification } from '@bringg/react-components';
import { BringgException } from '@bringg/dashboard-sdk/dist/Core/BringgException';
import { OptimizationErrors } from '@bringg/types';

export interface ErrorHandler {
	callback: () => Promise<any>;
	errorMessagesMap?: Record<string, string>;
	errorParams?: object;
	shouldRaiseError?: boolean;
}

type ErrorHandlerWithoutCallback = Omit<ErrorHandler, 'callback'>;

const toErrorMessages = (
	errorData,
	errorMessagesMap: Record<string, string>
): { notificationMessage: string; logMessage: string } => {
	const defaultMessage = errorMessagesMap.generalError;
	let notificationMessage = defaultMessage;
	let logMessage = defaultMessage;

	if (OptimizationErrors[errorData?.error_code]) {
		const errorCode = errorData.error_code;
		logMessage = OptimizationErrors[errorCode].message;
		notificationMessage = errorMessagesMap[errorCode] ?? logMessage;
	} else if (errorData?.error_message) {
		try {
			const optimizationErrors = JSON.parse(errorData.error_message);
			logMessage = optimizationErrors[0]?.message ?? logMessage;
			notificationMessage = errorMessagesMap[optimizationErrors[0]?.error_code] ?? defaultMessage;
		} catch (e) {
			// Ignore, there is already a fallback
			console.error(`The error message ${errorData.error_message} is not a JSON`, e);
		}
	}

	return { notificationMessage, logMessage };
};

const handleError = (err, { errorMessagesMap, errorParams, shouldRaiseError }: ErrorHandlerWithoutCallback) => {
	const errorData = (err as BringgException).data;
	const { logMessage, notificationMessage } = toErrorMessages(errorData, errorMessagesMap);
	Notification.error(notificationMessage, '', {
		message: notificationMessage,
		duration: null
	});
	const logParams = _compact([errorParams, err]);
	console.error(logMessage, logParams);
	if (shouldRaiseError) {
		throw new Error(notificationMessage);
	}
};

export const errorNotifierWrapper = async ({
	callback,
	errorMessagesMap,
	errorParams,
	shouldRaiseError = true
}: ErrorHandler) => {
	try {
		const result = await callback();
		return result;
	} catch (e) {
		handleError(e, { errorMessagesMap, errorParams, shouldRaiseError });
	}
};
